import { autocompletion, snippetCompletion } from '@codemirror/autocomplete';
import _ from 'lodash-es';
import Defaults from '~/scripts/constants/defaults';
import NodeDefinition from '~/scripts/services/model/node-definition';
import NodeDefinitionClassDico from '~/scripts/services/model/node-definition-class-dico';

const defaultCOMMON = {
	card_from: 1,
	card_to: 1,
	data: undefined,
	dico_id: undefined,
	internal: false,
	lock: 0,
	type: 'link',
};

const defaultDICO = {
	data: undefined,
	internal: false,
	linker_info: undefined,
	linker_kid: '0',
	lock: 0,
	objnum2_info: undefined,
	objnum2_kid: undefined,
	relation_kid: undefined,
};

const checkRules = (/** @type {any} */ myDico, /** @type {any[]} */ requirements) => {
	const errors = [];

	for (const requirement of requirements) {
		const error = requirement(myDico);
		if (typeof error === 'string') {
			errors.push(error);
		}
	}
	return errors;
};

export default class OoAdminKndefController {
	/* @ngInject */
	/**
	 * @param {import('angular').ILocationService} $location
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {import('~/scripts/services/ktranslate').KTranslate} KTranslate
	 * @param {import('~/scripts/services/model/node-service').NodeService} NodeService
	 * @param {any} Notification
	 */
	constructor(
		$location,
		$scope,
		$timeout,
		$translate,
		KTranslate,
		NodeService,
		Notification,
	) {
		this.$location = $location;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.KTranslate = KTranslate;
		this.NodeService = NodeService;
		this.Notification = Notification;

		this.classModes = ['NORMAL', 'DYNAMIC'];
		this.defaults = Defaults;

		const tagOptions = [
			'ACRONYM', 'false', 'true', 'CODE', 'UNIVERSEL',
		].map(tag => ({ label: tag, type: 'keyword' }));

		this.jsonEditorOptions = {
			mode: 'tree',
			modes: [
				'code',
				'form',
				'text',
				'tree',
				'view',
			],
			onError: (/** @type {any} */ error) => {
				Notification.warning({
					title: $translate.instant('error.title'),
					message: error.toString(),
				});
			},
			extensions: [
				autocompletion({
					override: [
						context => {
							const matchBefore = context.matchBefore(/\w*/);

							if (!matchBefore) {
								return null;
							}

							return {
								from: matchBefore.from,
								options: tagOptions,
							};
						},
						context => {
							const matchBefore = context.matchBefore(/^\w*/);

							if (!matchBefore) {
								return null;
							}

							return {
								from: matchBefore.from,
								options: [
									snippetCompletion(`{
	"USE": [
		{
			"kid": "K_NODE;UNIVERSEL",
			"required": \${1:true}
		},
		{
			"kid": "K_NODE;\${2:CODE}",
			"required": \${3:true}
		}
	]
}`, {
										label: 'repre',
									}),
									snippetCompletion(`{
	"USE": {
		"MODE": "USE",
		"REPRE": "K_NODE;\${UNIVERSEL}",
		"LINK": null
	}
}`,
									{
										label: 'key',
									}),
									snippetCompletion(`{
	"OBJECT": {
		"USE": {
			"eligible": \${1:true},
			"list": [
				"K_NODE;\${2}"
			]
		}
	}
}`,
									{
										label: 'kwf',
									}),
									snippetCompletion(`{
{
	"DICO": {
		"\${1:dico_id}": {
			"USE": "_NOW_"
		}
	}
}`,
									{
										label: 'datejr',
									}),
									snippetCompletion(`{
	"OBJECT": {
		"USE": "[K_NODE;\${1}][K_NODE;\${2}]"
	}
}`,
									{
										label: 'nmfy',
									}),
									snippetCompletion(`{
	"USE": {
		"order": "NODE",
		"node_kid": "K_NODE;\${1}",
		"restrictid": [
			"K_NODE;\${1}",
			"K_NODE;\${2}"
		],
		"selectable": [
			"K_NODE;\${1}"
		]
	}
}`,
									{
										label: 'restmf',
									}),
								],
							};
						},
					],
				}),
			],
		};

		/** @type {import('~/scripts/services/model/node-definition').default | undefined} */
		this.kndef = undefined;

		this.krepreModes = ['NONE', 'NORMAL', 'NO_INDEX'];

		/** @type {any} */
		this.options = undefined;

		/** @type {?import('../oo-admin.controller').default} */
		this.parent = null;

		/** @type {string | undefined} */
		this.tab2 = undefined;

		/** @type {string | undefined} */
		this.tab3 = undefined;

		/** @type {{id: string, name: string}[]} */
		this.translatedDico = [];

		/** @type {{kndef: NodeDefinition}} */
		this.tmp = {
			kndef: new NodeDefinition(),
		};
		this.type = 'kndef';
	}

	addCommon() {
		const myDico = this.tmp.kndef.CLASS.COMMON;

		const errors = checkRules(myDico, [
			(/** @type {any} */ dico) => !!dico.dico_id
				|| 'Un identifiant doit être sélectionné',
			(/** @type {any} */ dico) => (Object.prototype.hasOwnProperty.call(dico, 'card_from') && dico.card_from !== null)
				|| 'Une cardinalité d\'entrée doit être définie',
			(/** @type {any} */ dico) => (Object.prototype.hasOwnProperty.call(dico, 'card_to') && dico.card_to !== null)
				|| 'Une cardinalité de sortie doit être définie',
			(/** @type {any} */ dico) => (Object.prototype.hasOwnProperty.call(dico, 'lock') && dico.lock !== null)
				|| 'La valeur de verrouillage doit être définie',
		]);

		if (errors.length > 0) {
			this.Notification.error({
				title: 'Des erreurs ont été détectées',
				message: errors.join('<br>'),
			});
		}
		else {
			this.kndef?.add('CLASS', 'COMMON', this.tmp.kndef.CLASS.COMMON);
			this.tmp.kndef.CLASS.COMMON = _.extend({}, this.tmp.kndef.CLASS.COMMON, defaultCOMMON);
			// this.tmp.kndef.CLASS.COMMON.dico_id = undefined;
		}
	}

	addDico() {
		const myDico = this.tmp.kndef.CLASS.DICO;

		const errors = checkRules(myDico, [
			(/** @type {any} */ dico) => !!dico.relation_kid
				|| 'Une relation doit être sélectionné',
			(/** @type {any} */ dico) => (!!dico.objnum2_kid && dico.objnum2_kid.length > 0)
				|| 'Une classe d\'entrée doit être sélectionnée',
			(/** @type {any} */ dico) => !!dico.linker_kid
				|| 'Une classe de lien doit être sélectionnée',
		]);

		if (errors.length > 0) {
			this.Notification.error({
				title: 'Des erreurs ont été détectées',
				message: errors.join('<br>'),
			});
		}
		else if (this.kndef) {
			this.kndef.add('CLASS', 'DICO', new NodeDefinitionClassDico(myDico));
			this.tmp.kndef.CLASS.DICO.key = this.kndef.getNextKey('CLASS', 'DICO');
			this.tmp.kndef.CLASS.DICO = _.extend({}, myDico, defaultDICO);
			(async () => this.generateTranslatedDico())();
		}
	}

	/**
	 * @param {any} item
	 */
	dicoAddInfos(item) {
		this.tmp.kndef.CLASS.DICO.objnum2_info = {
			name: item.name,
		};
		(async () => this.generateTranslatedDico())();
	}

	/**
	 * @param {any} item
	 */
	dicoAddInfosLinker(item) {
		if (item) {
			this.tmp.kndef.CLASS.DICO.linker_info = {
				name: item.name,
			};
		}
		else {
			delete this.tmp.kndef.CLASS.DICO.linker_info;
		}
		(async () => this.generateTranslatedDico())();
	}

	/**
	 * @param {string} key
	 * @returns {string}
	 */
	generateSelectLabel(key) {
		const dico = this.kndef?.CLASS.DICO[key];
		let string = `${key} - ${dico.relation_kid}`;
		if (dico.objnum2_info) {
			string += ` ${dico.objnum2_info.name}`;
			if (dico.linker_info) {
				string += `::${dico.linker_info.name}`;
			}
		}

		return string;
	}

	async generateTranslatedDico() {
		/** @type {Record<string, string>} */
		if (this.kndef?.CLASS.DICO) {
			for (const [key, dico] of Object.entries(this.kndef?.CLASS.DICO)) {
				let name = `${key} - ${await this.KTranslate.request(this.$scope, dico.relation_kid)}`;
				name += ` ${dico.objnum2_info?.name ?? await this.KTranslate.request(this.$scope, dico.objnum2_kid)}`;

				if (dico.linker_kid && dico.linker_kid !== '0') {
					name += `::${dico.linker_info?.name ?? await this.KTranslate.request(this.$scope, dico.linker_kid)}`;
				}
				const index = this.translatedDico.findIndex(d => d.id === key);
				if (index >= 0) {
					this.translatedDico[index].name = name;
				}
				else {
					this.translatedDico.push({ id: key, name });
				}
			}
		}
	}

	/**
	 *
	 * @param {string} key - ID to remove
	 * @param {boolean} removeAssociatedCommon
	 */
	removeDicoKey(key, removeAssociatedCommon = false) {
		this.kndef?.remove('CLASS', 'DICO', key);

		if (this.kndef && removeAssociatedCommon) {
			for (const [index, common] of this.kndef.CLASS.COMMON.entries()) {
				if (common.dico_id === key) {
					this.removeCommonKey(index);
				}
			}
		}
		this.translatedDico.splice(this.translatedDico.findIndex(d => d.id === key), 1);
		this.$scope.$emit('admin-oo-set-form-dirty');
	}

	/**
	 * @param {any} dico
	 * @returns {Promise<any>}
	 */
	removeDicoLinkerKid(dico) {
		dico.linker_kid = '0';
		return this.generateTranslatedDico();
	}

	/**
	 *
	 * @param {number} key - Index to remove
	 */
	removeCommonKey(key) {
		this.kndef?.remove('CLASS', 'COMMON', key);
		this.$scope.$emit('admin-oo-set-form-dirty');
	}

	/**
	 * @param {import('angular').IOnChangesObject} changes
	 */
	$onChanges(changes) {
		if (changes.kndef && this.kndef) {
			this.tmp.kndef.CLASS.DICO.key = this.kndef.getNextKey('CLASS', 'DICO');
			this.tmp.kndef.CLASS.COMMON = _.merge({}, this.tmp.kndef.CLASS.COMMON, defaultCOMMON);
			this.tmp.kndef.CLASS.DICO = _.merge({}, this.tmp.kndef.CLASS.DICO, defaultDICO);
			if (changes.kndef.currentValue !== undefined && changes.kndef.previousValue === undefined) {
				this.$scope.$watch('kndef.CLASS.DICO', () => {
					// Filter out common with undefined dico entries
					this.$timeout(() => {
						if (this.kndef?.CLASS?.COMMON) {
							this.kndef.CLASS.COMMON = this.kndef.CLASS.COMMON.filter(
								(/** @type {any} */ element) => element.dico_id !== undefined,
							);
						}
						(async () => this.generateTranslatedDico())();
					});
				}, true);
			}
			(async () => this.generateTranslatedDico())();
		}
	}

	$onInit() {
		if (this.parent?.options?.root) {
			this.$scope.$watch('$ctrl.tab2', (/** @type {string} */ tab, /** @type {string} */ oldTab) => {
				if (tab && tab !== oldTab && this.parent?.node) {
					this.$location.path(`/admin/oo/${this.parent.node.kid}/${this.parent.tab}/${this.tab2}`);
				}
			});
			for (const stab of [
				'$ctrl.kndeftabCLASS',
				'$ctrl.kndeftabKREPRE',
				'$ctrl.kndeftabKREPRE',
			]) {
				this.$scope.$watch(stab, (/** @type {string} */ tab, /** @type {string} */ oldTab) => {
					if (tab && tab !== oldTab && this.parent?.node) {
						this.$location.path(`/admin/oo/${this.parent.node.kid}/${this.parent.tab}/${this.tab2}/${tab}`);
					}
				});
			}
		}
	}
}
